// S2-CC20.6b.1.5: StatusPill — 8-value enum → 4-color-group pill.
// Renders one llm_calls.status value as a pill with a leading colored dot
// and a human-readable label. The 8 ENUMs collapse to 4 color buckets
// because operators scan for CATEGORY (success / transient / intentional
// block / hard failure), not for individual ENUMs. Exact ENUM surfaces on
// hover via the `title` attribute.
//
// Wireframe: docs/sprint-2/wireframes/s2-cc6-llm-control-center.html (.pill, .pill-*)
import type { LlmCallStatus } from '../types/llm';
import './StatusPill.css';

// Color mapping is enforced internally — NOT exposed as a prop — to keep
// the 8 ENUM → 4 color contract in one place. Changing the mapping means
// editing this table and the matching `.pill-*` classes in StatusPill.css.
const COLOR_GROUP: Record<LlmCallStatus, 'success' | 'warning' | 'copper' | 'error'> = {
  success: 'success',
  // Warning bucket: retryable / transient / operational states.
  rate_limited: 'warning',
  gateway_paused: 'warning',
  timeout: 'warning',
  // Copper bucket: intentional denials — "we did this on purpose".
  budget_blocked: 'copper',
  policy_blocked: 'copper',
  // Error bucket: hard failures that need human intervention.
  auth_failed: 'error',
  provider_error: 'error',
};

// Human-readable labels. Derived from the ENUM (snake_case → Title case
// with first word capitalized) so adding an ENUM only requires editing
// COLOR_GROUP above + adding a LABEL entry here.
const LABEL: Record<LlmCallStatus, string> = {
  success: 'Success',
  rate_limited: 'Rate limited',
  auth_failed: 'Auth failed',
  budget_blocked: 'Budget blocked',
  policy_blocked: 'Policy blocked',
  gateway_paused: 'Gateway paused',
  provider_error: 'Provider error',
  timeout: 'Timeout',
};

export interface StatusPillProps {
  status: LlmCallStatus;
  // Default 'md' matches the Call Log table row density. 'sm' is for
  // inline use in the Budget Tree status slot where vertical space is tight.
  size?: 'sm' | 'md';
}

export function StatusPill({ status, size = 'md' }: StatusPillProps) {
  const group = COLOR_GROUP[status];
  const label = LABEL[status];
  return (
    <span
      className={`airgent-pill airgent-pill-${group} airgent-pill-${size}`}
      // Raw ENUM on hover — keeps the category-first UI but preserves the
      // exact value for operators who need to disambiguate (e.g.
      // rate_limited vs timeout, both amber).
      title={status}
    >
      <span className="airgent-pill-dot" aria-hidden="true" />
      {label}
    </span>
  );
}
